/**
 * Audit Filters Component
 * Provides filtering options for audit logs
 */

import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Search, X, Filter } from 'lucide-react';
import { useAuditStore } from '@/stores/auditStore';
import { AuditLog } from '@/types/audit';
import { useDebounce } from '@/hooks/useDebounce';

export const AuditFilters: React.FC = () => {
  const { filters, setFilters, resetFilters } = useAuditStore();
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  // Update store when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters({ search: debouncedSearch });
    }
  }, [debouncedSearch, filters.search, setFilters]);

  const handleActionChange = (value: string) => {
    const actions = value === 'all' ? [] : [value as AuditLog['action']];
    setFilters({ actions });
  };

  const handleStatusChange = (value: string[]) => {
    setFilters({ status: value });
  };

  const handleClearFilters = () => {
    setSearchInput('');
    resetFilters();
  };

  const hasActiveFilters = 
    filters.search || 
    (filters.actions && filters.actions.length > 0) || 
    (filters.status && filters.status.length > 0) ||
    filters.dateRange;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search audit logs..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Action Filter */}
          <Select 
            value={filters.actions?.[0] || 'all'} 
            onValueChange={handleActionChange}
          >
            <SelectTrigger className="w-[180px]">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="Plan Created">Plan Created</SelectItem>
              <SelectItem value="Step Completed">Step Completed</SelectItem>
              <SelectItem value="Tool Used">Tool Used</SelectItem>
              <SelectItem value="User Action">User Action</SelectItem>
              <SelectItem value="System Event">System Event</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <ToggleGroup 
            type="multiple" 
            value={filters.status || []}
            onValueChange={handleStatusChange}
            className="hidden sm:flex"
          >
            <ToggleGroupItem value="success" aria-label="Success">
              <Badge variant="outline" className="border-green-600 text-green-600">
                Success
              </Badge>
            </ToggleGroupItem>
            <ToggleGroupItem value="error" aria-label="Error">
              <Badge variant="outline" className="border-red-600 text-red-600">
                Error
              </Badge>
            </ToggleGroupItem>
            <ToggleGroupItem value="pending" aria-label="Pending">
              <Badge variant="outline" className="border-yellow-600 text-yellow-600">
                Pending
              </Badge>
            </ToggleGroupItem>
            <ToggleGroupItem value="warning" aria-label="Warning">
              <Badge variant="outline" className="border-orange-600 text-orange-600">
                Warning
              </Badge>
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Mobile Status Filter */}
          <Select 
            value={filters.status?.[0] || 'all'} 
            onValueChange={(value) => handleStatusChange(value === 'all' ? [] : [value])}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-8"
            >
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {filters.search && (
            <Badge variant="secondary">
              Search: {filters.search}
            </Badge>
          )}
          {filters.actions?.map((action) => (
            <Badge key={action} variant="secondary">
              Action: {action}
            </Badge>
          ))}
          {filters.status?.map((status) => (
            <Badge key={status} variant="secondary">
              Status: {status}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};